import { useEffect, useState, useCallback } from "react"
import { useLocation } from "react-router-dom"

import { motion, AnimatePresence } from "framer-motion"

import { Search, Bell, ChevronDown, Menu, X, ChevronLeft, ChevronRight, ChevronUp, Calculator } from "lucide-react"
import Sidebar from "./Sidebar"

import Breadcrumb from "./BreadCrumb"

import ProfileDropdown from "./ProfileDropdown"

import logo from "../../assets/logo.png"

import axios from "axios"

import config from "../../config"

import { usePageSave } from "../../contexts/PageSaveContext"



// Live Watch Component

const LiveWatch = () => {

  const [currentTime, setCurrentTime] = useState(new Date())



  useEffect(() => {

    const timer = setInterval(() => {

      setCurrentTime(new Date())

    }, 1000)



    return () => clearInterval(timer)

  }, [])



  const formatTime = (date) => {

    return date.toLocaleTimeString('en-US', {

      hour12: true,

      hour: '2-digit',

      minute: '2-digit',

      second: '2-digit'

    })

  }



  const formatDate = (date) => {

    return date.toLocaleDateString('en-US', {

      weekday: 'long',

      year: 'numeric',

      month: 'long',

      day: 'numeric'

    })

  }



  return (

    <div className="flex items-center gap-3 bg-gradient-to-r from-blue-500 to-purple-600 text-black px-4 py-2 rounded-lg shadow-lg">

      <div className="flex items-center gap-2">

        <div className="w-3 h-3 bg-green-400 rounded-full animate-pulse"></div>

        <div className="text-sm font-medium text-black">LIVE</div>

      </div>

      <div className="flex flex-col items-center">

        <div className="text-lg font-bold font-mono tracking-wider text-black">

          {formatTime(currentTime)}

        </div>

        <div className="text-xs opacity-90 text-black">

          {formatDate(currentTime)}

        </div>

      </div>

    </div>

  )

}



export default function Dashboard({ children, salaryData = null, showTaxComparison = false }) {

  const { isPageSaved } = usePageSave()
  const location = useLocation()

  const [isSidebarOpen, setIsSidebarOpen] = useState(false)

  const [isDropdownOpen, setIsDropdownOpen] = useState(false)

  const [selectedYear, setSelectedYear] = useState("FY 2024-25")

  const [isMobile, setIsMobile] = useState(false)

  const [panCardNumber, setPanCardNumber] = useState(localStorage.getItem("PAN_NO"))

  const [assessmentYear, setAssessmentYear] = useState(localStorage.getItem("YEAR"))

  const [profileData, setProfileData] = useState(null)

  const [loading, setLoading] = useState(true)

  const [sidebarKeepOpen, setSidebarKeepOpen] = useState(() => {

    const v = localStorage.getItem('sidebarKeepOpen')

    return v ? v === 'true' : false

  })

  const [isComparisonSidebarOpen, setIsComparisonSidebarOpen] = useState(false)

  const [sidebarWidth, setSidebarWidth] = useState(320)

  const [isResizing, setIsResizing] = useState(false)

  const [fallbackComparison, setFallbackComparison] = useState(null)

  const [isExemptAllowancesExpanded, setIsExemptAllowancesExpanded] = useState(false)

  const [isCalculationExpanded, setIsCalculationExpanded] = useState(false)

  const [deductionBreakdown, setDeductionBreakdown] = useState(null)

  // Local state for deduction totals to display in sidebar
  const [localDeductionTotals, setLocalDeductionTotals] = useState(null)

  // Determine if we're on the deduction page
  const isOnDeductionPage = location.pathname.includes('deduction-vi-a')



  const years = ["FY 2024-25", "FY 2023-24", "FY 2022-23", "FY 2021-22"]



  // Resize handlers

  const handleMouseDown = (e) => {

    setIsResizing(true)

    e.preventDefault()

  }



  const handleMouseMove = useCallback((e) => {

    if (!isResizing) return



    const newWidth = window.innerWidth - e.clientX

    if (newWidth >= 280 && newWidth <= window.innerWidth * 0.7) {

      setSidebarWidth(newWidth)

    }

  }, [isResizing])



  const handleMouseUp = useCallback(() => {

    setIsResizing(false)

  }, [])



  useEffect(() => {

    if (isResizing) {

      document.addEventListener('mousemove', handleMouseMove)

      document.addEventListener('mouseup', handleMouseUp)

      document.body.style.cursor = 'ew-resize'

      document.body.style.userSelect = 'none'

    }



    return () => {

      document.removeEventListener('mousemove', handleMouseMove)

      document.removeEventListener('mouseup', handleMouseUp)

      document.body.style.cursor = ''

      document.body.style.userSelect = ''

    }

  }, [isResizing, handleMouseMove, handleMouseUp])



  const fetchProfileData = async () => {

    try {

      setLoading(true)

      const response = await axios.get(`${config.apiUrl}/user/profile/${panCardNumber}/${assessmentYear}`)

      const data = response.data

      setProfileData(data)

      setLoading(false)

    } catch (error) {

      console.error('Failed to fetch profile data:', error)

      setLoading(false)

    }

  }



  useEffect(() => {

    setAssessmentYear(localStorage.getItem("YEAR"))

    setPanCardNumber(localStorage.getItem("PAN_NO"))

    if (panCardNumber && assessmentYear) {

      fetchProfileData()

    }

  }, [panCardNumber, assessmentYear])



  const toggleDropdown = () => setIsDropdownOpen(!isDropdownOpen)



  const handleYearSelect = (year) => {

    setSelectedYear(year)

    setIsDropdownOpen(false)

    // You might want to update the assessment year in localStorage here

    // and refetch profile data if needed

    // localStorage.setItem("YEAR", year)

    // setAssessmentYear(year)

  }



  const toggleSidebar = () => {

    setIsSidebarOpen(!isSidebarOpen)

    if (!isMobile) {

      localStorage.setItem("sidebarOpen", (!isSidebarOpen).toString())

    }

  }



  useEffect(() => {

    const handleResize = () => {

      const mobile = window.innerWidth < 1024 // lg breakpoint

      setIsMobile(mobile)



      if (mobile) {

        setIsSidebarOpen(false)

      } else {

        const savedState = localStorage.getItem("sidebarOpen")

        setIsSidebarOpen(savedState !== null ? savedState === "true" : true)

      }

    }



    handleResize()

    window.addEventListener("resize", handleResize)

    return () => window.removeEventListener("resize", handleResize)

  }, [])



  useEffect(() => {

    const handleClickOutside = (event) => {

      if (isMobile && isSidebarOpen) {

        const sidebar = document.getElementById("sidebar")

        const menuButton = document.getElementById("menu-button")



        if (sidebar && !sidebar.contains(event.target) && menuButton && !menuButton.contains(event.target)) {

          setIsSidebarOpen(false)

        }

      }

    }



    document.addEventListener("mousedown", handleClickOutside)

    return () => document.removeEventListener("mousedown", handleClickOutside)

  }, [isMobile, isSidebarOpen])



  useEffect(() => {

    const onKeepOpenChange = () => {

      const v = localStorage.getItem('sidebarKeepOpen')

      setSidebarKeepOpen(v ? v === 'true' : false)

    }

    window.addEventListener('sidebarKeepOpenChanged', onKeepOpenChange)

    return () => window.removeEventListener('sidebarKeepOpenChanged', onKeepOpenChange)

  }, [])



  // Fallback fetch for comparison data when no salaryData is provided (e.g., dashboard home)

  useEffect(() => {

    const loadComparison = async () => {

      try {

        if (!salaryData && showTaxComparison && panCardNumber && assessmentYear) {

          const [oldRes, newRes, deductionRes] = await Promise.all([
            axios.get(`${config.apiUrl}/user/tax-computation/${panCardNumber}/${assessmentYear}/OLD`),

            axios.get(`${config.apiUrl}/user/tax-computation/${panCardNumber}/${assessmentYear}/NEW`),
            axios.get(`${config.apiUrl}/deduction/${panCardNumber}/${assessmentYear}`)
          ])

          const oldIncome = Number(oldRes.data?.taxComputation?.incomeFromSalary || 0)

          const newIncome = Number(newRes.data?.taxComputation?.incomeFromSalary || 0)

          setFallbackComparison({

            formatCurrency: (v) => new Intl.NumberFormat('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Math.abs(v)),

            oldRegimeTaxable: oldIncome,

            newRegimeTaxable: newIncome,

            difference: oldIncome - newIncome,

          })

          setDeductionBreakdown(deductionRes.data?.deduction)

        }

      } catch (e) {

        // no-op

      }

    }

    loadComparison()

  }, [salaryData, showTaxComparison, panCardNumber, assessmentYear])

  // Fetch deduction data when comparison sidebar is shown and we're on deduction page
  useEffect(() => {
    const fetchDeductionData = async () => {
      try {
        if (showTaxComparison && panCardNumber && assessmentYear && isOnDeductionPage) {
          console.log('Fetching deduction data for comparison sidebar...')
          const deductionRes = await axios.get(`${config.apiUrl}/deduction/${panCardNumber}/${assessmentYear}`)
          console.log('Deduction API response for sidebar:', deductionRes.data?.deduction)
          
          // Calculate total deductions from the API response
          const deductionData = deductionRes.data?.deduction
          if (deductionData) {
            const totalDeductions = deductionData.deductionUndChapVIAType?.totalChapVIADeductions || 0
            console.log('Total deductions calculated:', totalDeductions)
            
            // Update local deduction totals for display
            setLocalDeductionTotals(totalDeductions)
          }
        }
      } catch (e) {
        console.error('Error fetching deduction data for sidebar:', e)
      }
    }
    fetchDeductionData()
  }, [showTaxComparison, panCardNumber, assessmentYear, isOnDeductionPage])

  // Function to refresh deduction data for sidebar
  const refreshDeductionDataForSidebar = useCallback(async () => {
    try {
      if (panCardNumber && assessmentYear) {
        console.log('Refreshing deduction data for sidebar...')
        const deductionRes = await axios.get(`${config.apiUrl}/deduction/${panCardNumber}/${assessmentYear}`)
        const deductionData = deductionRes.data?.deduction
        if (deductionData) {
          const totalDeductions = deductionData.deductionUndChapVIAType?.totalChapVIADeductions || 0
          console.log('Refreshed total deductions:', totalDeductions)
          
          setLocalDeductionTotals(totalDeductions)
        }
      }
    } catch (e) {
      console.error('Error refreshing deduction data for sidebar:', e)
    }
  }, [panCardNumber, assessmentYear])

  // Auto-refresh deduction data every 10 seconds when on deduction page and sidebar is open
  useEffect(() => {
    if (isOnDeductionPage && showTaxComparison && isComparisonSidebarOpen) {
      const interval = setInterval(() => {
        refreshDeductionDataForSidebar()
      }, 10000) // Refresh every 10 seconds

      return () => clearInterval(interval)
    }
  }, [isOnDeductionPage, showTaxComparison, isComparisonSidebarOpen, refreshDeductionDataForSidebar])

  // Removed visibility change and focus event listeners to prevent continuous API calls



  const formatCurrency = (value) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 2,
    }).format(value || 0)
  }


  return (

    <div className="min-h-screen bg-gray-50">

      {/* Mobile Overlay */}

      {isMobile && isSidebarOpen && (

        <div className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden" onClick={() => setIsSidebarOpen(false)} />

      )}



      {/* Header */}

      <header className="fixed top-0 left-0 right-0 bg-gray-100 z-30 border-b border-gray-200 h-20">
        <div className="flex justify-between gap-1 items-center h-full">

          <div className="flex items-center justify-center h-full">
            <img src={logo} className="w-32 h-32 object-contain" alt="" />
              </div>

          <div className={`transition-all duration-300 ease-in-out ${!isMobile ? (sidebarKeepOpen ? "lg:ml-64" : "lg:ml-14") : (isSidebarOpen ? '' : '')}`}>
            <div className="flex items-center pr-4 sm:pr-6 py-3">
              {/* Left Section - Live Watch */}
              <div className="flex items-center gap-2 mr-auto">
                <LiveWatch />

              </div>



              {/* Right Section */}

              <div className="flex items-center gap-2 sm:gap-4">

                {/* Search - Hidden on mobile */}

                <div className="relative hidden md:block">

                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />

                  <input

                    type="text"

                    placeholder="Search..."

                    className="pl-9 pr-4 py-2 bg-white rounded-lg w-48 lg:w-64 text-sm border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"

                  />

                </div>



                {/* Year Selector - Hidden on small screens */}

                <div className="relative hidden sm:block">

                  <button

                    onClick={toggleDropdown}

                    className="flex items-center justify-between w-32 sm:w-40 px-3 py-2 bg-white text-gray-700 border border-gray-200 rounded-lg shadow-sm hover:shadow-md focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all text-sm"

                  >

                    <span className="font-medium truncate">{selectedYear}</span>

                    <motion.div animate={{ rotate: isDropdownOpen ? 180 : 0 }} transition={{ duration: 0.2 }}>

                      <ChevronDown className="w-4 h-4 ml-2 flex-shrink-0" />

                    </motion.div>

                  </button>



                  <AnimatePresence>

                    {isDropdownOpen && (

                      <motion.div

                        initial={{ opacity: 0, y: -10 }}

                        animate={{ opacity: 1, y: 0 }}

                        exit={{ opacity: 0, y: -10 }}

                        transition={{ duration: 0.2 }}

                        className="absolute right-0 z-50 w-40 mt-2 bg-white border border-gray-200 rounded-lg shadow-lg"

                      >

                        <ul className="py-1">

                          {years.map((year) => (

                            <li key={year}>

                              <button

                                onClick={() => handleYearSelect(year)}

                                className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition-colors"

                              >

                                {year}

                              </button>

                            </li>

                          ))}

                        </ul>

                      </motion.div>

                    )}

                  </AnimatePresence>

                </div>



                {/* Notifications */}

                <div className="relative">

                  <button className="p-2 rounded-lg hover:bg-gray-200 transition-colors">

                    <Bell className="w-5 h-5 text-gray-700" />

                    <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center">

                      2

                    </span>

                  </button>

                </div>



                {/* Profile */}

                <ProfileDropdown data={profileData} />

              </div>

            </div>

          </div>

        </div>

      </header>



      {/* Main Layout */}

      <div className="flex pt-16">

        {/* Sidebar */}

        <Sidebar isOpen={isMobile ? isSidebarOpen : false} isMobile={isMobile} onClose={() => setIsSidebarOpen(false)} />



        {/* Tax Regime Comparison Sidebar */}

        {showTaxComparison && salaryData && (

          <div className="fixed right-0 top-16 h-full z-20">

            {/* Toggle Button - Always Visible */}

            <button

              onClick={() => setIsComparisonSidebarOpen(!isComparisonSidebarOpen)}

              className="absolute -left-8 top-1/2 -translate-y-1/2 w-8 h-16 bg-cyan-500 hover:bg-cyan-600 text-white rounded-l-lg shadow-xl border-2 border-cyan-300 flex items-center justify-center transition-all duration-200 z-40"

              style={{ backgroundColor: '#06b6d4', borderColor: '#67e8f9' }}

              onMouseEnter={(e) => e.target.style.backgroundColor = '#0891b2'}

              onMouseLeave={(e) => e.target.style.backgroundColor = '#06b6d4'}

              title={isComparisonSidebarOpen ? "Hide Tax Comparison" : "Show Tax Comparison"}

            >

              {isComparisonSidebarOpen ? (

                <ChevronRight size={16} className="text-white" />

              ) : (

                <ChevronLeft size={16} className="text-white" />

              )}

            </button>



            {/* Sliding Sidebar Content */}

            <motion.div

              initial={false}

              animate={{

                width: isComparisonSidebarOpen ? `${sidebarWidth}px` : "0px",

                opacity: isComparisonSidebarOpen ? 1 : 0

              }}

              transition={{ duration: 0.3, ease: "easeInOut" }}

              className="h-full bg-white border-l border-gray-200 shadow-lg overflow-hidden relative"

            >

              {/* Resize Handle */}

              <div

                onMouseDown={handleMouseDown}

                className={`absolute left-0 top-0 w-2 h-full bg-gradient-to-r from-cyan-400 to-cyan-300 hover:from-cyan-500 hover:to-cyan-400 cursor-ew-resize transition-all duration-200 ${isResizing ? 'from-cyan-600 to-cyan-500 w-3' : ''}`}

                style={{ zIndex: 50 }}

                title="Drag to resize sidebar"

              >

                {/* Resize Handle Visual Indicator */}

                <div className="absolute inset-y-0 left-1/2 transform -translate-x-1/2 w-0.5 bg-white opacity-60 rounded-full" />

              </div>



              {/* Sidebar Content */}

              <div

                className="h-full overflow-y-auto pl-2"

                style={{ width: `${sidebarWidth}px`, maxHeight: 'calc(100vh - 4rem)' }}

              >

                <div className="p-4 pb-6">

                  <div className="bg-gradient-to-r from-cyan-500 to-cyan-600 text-white p-3 rounded-t-lg">
                    <div className="flex justify-between items-center">
                      <div>
                    <h3 className="text-sm font-semibold">Tax Regime Comparison</h3>
                    <p className="text-cyan-100 text-xs">Old vs New Tax Regime</p>
                      </div>
                      <button
                        onClick={refreshDeductionDataForSidebar}
                        className="text-cyan-100 hover:text-white transition-colors p-1"
                        title="Refresh deduction data"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                      </button>
                    </div>
                  </div>



                  <div className="bg-white border border-gray-200 rounded-b-lg">

                    <div className="p-3">

                      {/* Table Header */}

                      <div className="grid grid-cols-3 gap-2 mb-3 pb-2 border-b border-gray-300">

                        <div className="text-xs font-bold text-gray-800">Particulars</div>

                        <div className="text-xs font-bold text-orange-700 text-center">

                          <div className="flex items-center justify-center gap-1">

                            <div className="w-2 h-2 bg-orange-500 rounded-full"></div>

                            Old Regime

                          </div>

                        </div>

                        <div className="text-xs font-bold text-green-700 text-center">

                          <div className="flex items-center justify-center gap-1">

                            <div className="w-2 h-2 bg-green-500 rounded-full"></div>

                            New Regime

                          </div>

                        </div>

                      </div>



                      {/* Table Rows */}

                      <div className="space-y-2">

                        {/* Dynamic content based on data type */}

                        {(salaryData || fallbackComparison)?.totalGrossSalary !== undefined ? (

                          // Salary-specific rows

                          <>

                            <div className="grid grid-cols-3 gap-2 py-2 border-b border-gray-100">

                              <div className="text-xs text-gray-700 font-medium">Gross Salary</div>

                              <div className="text-xs text-orange-800 font-semibold text-center">

                                {(salaryData || fallbackComparison).formatCurrency((salaryData || fallbackComparison).totalGrossSalary)}

                              </div>

                              <div className="text-xs text-green-800 font-semibold text-center">

                                {(salaryData || fallbackComparison).formatCurrency((salaryData || fallbackComparison).totalGrossSalary)}

                              </div>

                            </div>



                            <div className="grid grid-cols-3 gap-2 py-2 border-b border-gray-100">

                              <div className="text-xs text-gray-700 font-medium">Less: Exempt Allowances</div>

                              <div className="text-xs text-orange-800 font-semibold text-center">

                                {(salaryData || fallbackComparison).formatCurrency((salaryData || fallbackComparison).totalAllowanceItems)}

                                <span className="block text-xs opacity-75">(All allowances)</span>

                              </div>

                              <div className="text-xs text-green-600 font-semibold text-center">

                                {(salaryData || fallbackComparison).formatCurrency((salaryData || fallbackComparison).newRegimeExemptAllowances || 0)}

                                <span className="block text-xs opacity-75">(Specific only)</span>

                              </div>

                            </div>



                            <div className="grid grid-cols-3 gap-2 py-2 border-b border-gray-100">

                              <div className="text-xs text-gray-700 font-medium">Less: Relief u/s 89A</div>

                              <div className="text-xs text-orange-800 font-semibold text-center">

                                {(salaryData || fallbackComparison).formatCurrency((salaryData || fallbackComparison).totalRelief)}

                              </div>

                              <div className="text-xs text-green-800 font-semibold text-center">

                                {(salaryData || fallbackComparison).formatCurrency((salaryData || fallbackComparison).totalRelief)}

                              </div>

                            </div>



                            <div className="grid grid-cols-3 gap-2 py-2 border-b border-gray-100">

                              <div className="text-xs text-gray-700 font-medium">Less: Deduction u/s 16</div>

                              <div className="text-xs text-orange-800 font-semibold text-center">

                                {(salaryData || fallbackComparison).formatCurrency((salaryData || fallbackComparison).totalDeduction)}

                                <span className="block text-xs opacity-75">(All deductions)</span>

                              </div>

                              <div className="text-xs text-green-800 font-semibold text-center">

                                {(salaryData || fallbackComparison).formatCurrency((salaryData || fallbackComparison).newRegimeStandardDeduction || 0)}

                                <span className="block text-xs opacity-75">(Min of ₹75,000)</span>

                              </div>

                            </div>



                            <div className="grid grid-cols-3 gap-2 py-2 pt-3 border-t-2 border-gray-300 bg-gray-50 rounded">

                              <div className="text-xs text-gray-800 font-bold">Taxable Salary</div>

                              <div className="text-xs text-orange-900 font-bold text-center">

                                {(salaryData || fallbackComparison).formatCurrency((salaryData || fallbackComparison).oldRegimeTaxable)}

                              </div>

                              <div className="text-xs text-green-900 font-bold text-center">

                                {(salaryData || fallbackComparison).formatCurrency((salaryData || fallbackComparison).newRegimeTaxable)}

                              </div>

                            </div>



                            {/* Collapsible Exempt Allowances Comparison */}

                            <div className="mt-3">

                              <button

                                onClick={() => setIsExemptAllowancesExpanded(!isExemptAllowancesExpanded)}

                                className="w-full flex items-center justify-between p-2 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100 transition-colors"

                              >

                                <span className="text-xs font-semibold text-gray-800">Exempt Allowances Details</span>

                                {isExemptAllowancesExpanded ? (

                                  <ChevronUp className="w-3 h-3 text-gray-600" />

                                ) : (

                                  <ChevronDown className="w-3 h-3 text-gray-600" />

                                )}

                              </button>



                              <AnimatePresence>

                                {isExemptAllowancesExpanded && (

                                  <motion.div

                                    initial={{ opacity: 0, height: 0 }}

                                    animate={{ opacity: 1, height: "auto" }}

                                    exit={{ opacity: 0, height: 0 }}

                                    transition={{ duration: 0.3 }}

                                    className="overflow-hidden"

                                  >

                                    <div className="mt-2 space-y-2">

                                      {/* Old Regime Allowances */}

                                      <div className="p-2 bg-orange-50 border border-orange-200 rounded-lg">

                                        <div className="text-xs font-semibold text-orange-800 mb-2">Old Regime - All Exempt Allowances:</div>

                                        <div className="text-xs text-orange-700 space-y-1">

                                          <div>• Sec 10(5) - Leave Travel concession</div>

                                          <div>• Sec 10(6) - Embassy remuneration</div>

                                          <div>• Sec 10(7) - Overseas allowances</div>

                                          <div>• Sec 10(10) - Death-cum-retirement gratuity</div>

                                          <div>• Sec 10(10A) - Commuted pension</div>

                                          <div>• Sec 10(10AA) - Leave encashment</div>

                                          <div>• Sec 10(10B) - Compensation</div>

                                          <div>• Sec 10(10C) - VRS amount</div>

                                          <div>• Sec 10(10CC) - Tax on perquisites</div>

                                          <div>• Sec 10(13A) - HRA</div>

                                          <div>• Sec 10(14)(i) & 10(14)(ii) - Transport allowances</div>

                                          <div>• Sec 10(14)(i)(115BAC) & 10(14)(ii)(115BAC)</div>

                                          <div>• EIC - Entertainment allowance</div>

                                          <div>• Any Other exempt allowances</div>

                                        </div>

                                      </div>



                                      {/* New Regime Allowances */}

                                      <div className="p-2 bg-green-50 border border-green-200 rounded-lg">

                                        <div className="text-xs font-semibold text-green-800 mb-2">New Regime - Limited Exempt Allowances:</div>

                                        <div className="text-xs text-green-700 space-y-1">

                                          <div>• Sec 10(6) - Embassy remuneration</div>

                                          <div>• Sec 10(7) - Overseas allowances</div>

                                          <div>• Sec 10(10) - Death-cum-retirement gratuity</div>

                                          <div>• Sec 10(10A) - Commuted pension</div>

                                          <div>• Sec 10(10AA) - Leave encashment</div>

                                          <div>• Sec 10(10B) - Compensation</div>

                                          <div>• Sec 10(10C) - VRS amount</div>

                                          <div>• Sec 10(10CC) - Tax on perquisites</div>

                                          <div>• Sec 10(14)(i)(115BAC) & 10(14)(ii)(115BAC)</div>

                                          <div>• Any Other exempt allowances</div>

                                        </div>

                                        <div className="mt-2 p-1 bg-red-100 border border-red-200 rounded text-xs text-red-700">

                                          <strong>Not Allowed:</strong> HRA, LTA, Transport allowances, Entertainment allowance

                                        </div>

                                      </div>

                                    </div>

                                  </motion.div>

                                )}

                              </AnimatePresence>

                            </div>



                            {/* Collapsible Calculation Breakdown */}

                            <div className="mt-2">

                              <button

                                onClick={() => setIsCalculationExpanded(!isCalculationExpanded)}

                                className="w-full flex items-center justify-between p-2 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100 transition-colors"

                              >

                                <span className="text-xs font-semibold text-gray-800">Calculation Breakdown</span>

                                {isCalculationExpanded ? (

                                  <ChevronUp className="w-3 h-3 text-gray-600" />

                                ) : (

                                  <ChevronDown className="w-3 h-3 text-gray-600" />

                                )}

                              </button>



                              <AnimatePresence>

                                {isCalculationExpanded && (

                                  <motion.div

                                    initial={{ opacity: 0, height: 0 }}

                                    animate={{ opacity: 1, height: "auto" }}

                                    exit={{ opacity: 0, height: 0 }}

                                    transition={{ duration: 0.3 }}

                                    className="overflow-hidden"

                                  >

                                    <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded-lg">

                                      <div className="text-xs font-semibold text-blue-800 mb-2">New Regime Calculation:</div>

                                      <div className="text-xs text-blue-700 space-y-1">

                                        <div>Gross Salary: {(salaryData || fallbackComparison).formatCurrency((salaryData || fallbackComparison).totalGrossSalary)}</div>

                                        <div>Less: Exempt Allowances: -{(salaryData || fallbackComparison).formatCurrency((salaryData || fallbackComparison).newRegimeExemptAllowances || 0)}</div>

                                        <div>Less: Relief u/s 89A: -{(salaryData || fallbackComparison).formatCurrency((salaryData || fallbackComparison).totalRelief)}</div>

                                        <div className="border-t border-blue-300 pt-1 mt-1">

                                          <div>Base Amount: {(salaryData || fallbackComparison).formatCurrency(((salaryData || fallbackComparison).totalGrossSalary || 0) - ((salaryData || fallbackComparison).newRegimeExemptAllowances || 0) - ((salaryData || fallbackComparison).totalRelief || 0))}</div>

                                          <div>Standard Deduction: Min(Base Amount, ₹75,000) = {(salaryData || fallbackComparison).formatCurrency((salaryData || fallbackComparison).newRegimeStandardDeduction || 0)}</div>

                                        </div>

                                      </div>

                                    </div>

                                  </motion.div>

                                )}

                              </AnimatePresence>

                            </div>

                          </>

                        ) : (salaryData || fallbackComparison)?.totalHousePropertyIncome !== undefined ? (

                          // House Property specific rows

                          <>

                            <div className="grid grid-cols-3 gap-2 py-2 border-b border-gray-100">

                              <div className="text-xs text-gray-700 font-medium">Annual Value</div>

                              <div className="text-xs text-orange-800 font-semibold text-center">

                                {(salaryData || fallbackComparison).formatCurrency((salaryData || fallbackComparison).totalAnnualValue)}

                              </div>

                              <div className="text-xs text-green-800 font-semibold text-center">

                                {(salaryData || fallbackComparison).formatCurrency((salaryData || fallbackComparison).totalAnnualValue)}

                              </div>

                            </div>



                            <div className="grid grid-cols-3 gap-2 py-2 border-b border-gray-100">

                              <div className="text-xs text-gray-700 font-medium">Less: Standard Deduction (30%)</div>

                              <div className="text-xs text-orange-800 font-semibold text-center">

                                {(salaryData || fallbackComparison).formatCurrency((salaryData || fallbackComparison).totalStandardDeduction)}

                              </div>

                              <div className="text-xs text-green-800 font-semibold text-center">

                                {(salaryData || fallbackComparison).formatCurrency((salaryData || fallbackComparison).totalStandardDeduction)}

                              </div>

                            </div>



                            <div className="grid grid-cols-3 gap-2 py-2 border-b border-gray-100">

                              <div className="text-xs text-gray-700 font-medium">Less: Interest Payable</div>

                              <div className="text-xs text-orange-800 font-semibold text-center">

                                {(salaryData || fallbackComparison).formatCurrency((salaryData || fallbackComparison).totalInterestPayable)}

                                <span className="block text-xs opacity-75">(All properties)</span>

                              </div>

                              <div className="text-xs text-green-800 font-semibold text-center">

                                {(salaryData || fallbackComparison).formatCurrency((salaryData || fallbackComparison).newRegimeInterestPayable || 0)}

                                <span className="block text-xs opacity-75">(Self-occupied: 0)</span>

                              </div>

                            </div>



                            <div className="grid grid-cols-3 gap-2 py-2 pt-3 border-t-2 border-gray-300 bg-gray-50 rounded">

                              <div className="text-xs text-gray-800 font-bold">House Property Income</div>

                              <div className="text-xs text-orange-900 font-bold text-center">

                                {(salaryData || fallbackComparison).formatCurrency((salaryData || fallbackComparison).oldRegimeTaxable)}

                              </div>

                              <div className="text-xs text-green-900 font-bold text-center">

                                {(salaryData || fallbackComparison).formatCurrency((salaryData || fallbackComparison).newRegimeTaxable)}

                              </div>

                            </div>

                          </>

                        ) : salaryData.totalOtherSourcesIncome !== undefined ? (

                          // Other Sources specific rows

                          <>

                            <div className="grid grid-cols-3 gap-2 py-2 border-b border-gray-100">

                              <div className="text-xs text-gray-700 font-medium">Less: Family Pension Deduction</div>

                              <div className="text-xs text-orange-800 font-semibold text-center">

                                {(salaryData || fallbackComparison).formatCurrency((salaryData || fallbackComparison).totalDeductions)}

                                <span className="block text-xs opacity-75">(Min of ₹15,000)</span>

                              </div>

                              <div className="text-xs text-green-800 font-semibold text-center">

                                {(salaryData || fallbackComparison).formatCurrency((salaryData || fallbackComparison).newRegimeFamilyPensionDeduction || 0)}

                                <span className="block text-xs opacity-75">(Min of ₹25,000)</span>

                              </div>

                            </div>



                            <div className="grid grid-cols-3 gap-2 py-2 pt-3 border-t-2 border-gray-300 bg-gray-50 rounded">

                              <div className="text-xs text-gray-800 font-bold">Other Sources Income</div>

                              <div className="text-xs text-orange-900 font-bold text-center">

                                {(salaryData || fallbackComparison).formatCurrency((salaryData || fallbackComparison).oldRegimeTaxable || 0)}

                              </div>

                              <div className="text-xs text-green-900 font-bold text-center">

                                {(salaryData || fallbackComparison).formatCurrency((salaryData || fallbackComparison).newRegimeTaxable || 0)}

                              </div>

                            </div>

                          </>

                        ) : (salaryData.totalDeductions !== undefined || localDeductionTotals !== null) ? (
                          // Deduction specific rows
                          <>
                            <div className="grid grid-cols-3 gap-2 py-2 border-b border-gray-100">
                              <div className="text-xs text-gray-700 font-medium">Less: Deductions VI-A</div>
                              <div className="text-xs text-orange-800 font-semibold text-center">
                                {formatCurrency(localDeductionTotals || (salaryData || fallbackComparison)?.totalDeductions || 0)}
                              </div>
                              <div className="text-xs text-green-800 font-semibold text-center">
                                {formatCurrency(0)}
                              </div>
                            </div>
                            <div className="grid grid-cols-3 gap-2 py-2 pt-3 border-t-2 border-gray-300 bg-gray-50 rounded">
                              <div className="text-xs text-gray-800 font-bold">Total Deductions</div>
                              <div className="text-xs text-orange-900 font-bold text-center">
                                {formatCurrency(localDeductionTotals || (salaryData || fallbackComparison)?.totalDeductions || 0)}
                              </div>
                              <div className="text-xs text-green-900 font-bold text-center">
                                {formatCurrency(0)}
                              </div>
                            </div>
                          </>
                        ) : (
                          // Generic income rows for other pages
                          <div className="grid grid-cols-3 gap-2 py-2 pt-3 border-t-2 border-gray-300 bg-gray-50 rounded">
                            <div className="text-xs text-gray-800 font-bold">
                              {salaryData.totalCapitalGains !== undefined ? 'Capital Gains' : 'Income/Deduction'}
                            </div>
                            <div className="text-xs text-orange-900 font-bold text-center">
                              {(salaryData || fallbackComparison)?.formatCurrency((salaryData || fallbackComparison)?.oldRegimeTaxable || 0)}
                            </div>
                            <div className="text-xs text-green-900 font-bold text-center">
                              {(salaryData || fallbackComparison)?.formatCurrency((salaryData || fallbackComparison)?.newRegimeTaxable || 0)}
                            </div>
                          </div>
                        )}

                      </div>



                      {/* Tax Saving Summary */}

                      <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">

                        <div className="flex items-center justify-between">

                          <div className="flex items-center gap-2">

                            <div className="w-2 h-2 bg-blue-500 rounded-full"></div>

                            <span className="text-xs text-blue-800 font-medium">Tax Saving Potential</span>

                          </div>

                          <div className="text-right">

                            <div className="text-xs text-blue-600">

                              {(salaryData || fallbackComparison)?.difference > 0 ? "Old Regime saves:" : (salaryData || fallbackComparison)?.difference < 0 ? "New Regime saves:" : "Both regimes equal"}

                            </div>

                            <div className="text-sm font-bold text-blue-800">

                              {(salaryData || fallbackComparison)?.formatCurrency(Math.abs((salaryData || fallbackComparison)?.difference || 0))}

                            </div>

                          </div>

                        </div>

                      </div>


                    </div>
                  </div>
                </div>

              </div>

            </motion.div>

          </div>

        )}



        {/* Main Content */}

        <main

          className={`flex-1 transition-all duration-300 ease-in-out ${!isMobile ? (sidebarKeepOpen ? "lg:ml-64" : "lg:ml-14") : (isSidebarOpen ? '' : '')} ${showTaxComparison && isComparisonSidebarOpen ? "mr-80" : ""}`}

        >

          <div className="p-4 sm:p-6">

            <div className="mb-4">

              <Breadcrumb />

            </div>



            {/* Mobile Year Selector */}

            <div className="sm:hidden mb-4">

              <div className="relative">

                <button

                  onClick={toggleDropdown}

                  className="flex items-center justify-between w-full px-4 py-3 bg-white text-gray-700 border border-gray-200 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"

                >

                  <span className="font-medium">{selectedYear}</span>

                  <motion.div animate={{ rotate: isDropdownOpen ? 180 : 0 }} transition={{ duration: 0.2 }}>

                    <ChevronDown className="w-4 h-4" />

                  </motion.div>

                </button>



                <AnimatePresence>

                  {isDropdownOpen && (

                    <motion.div

                      initial={{ opacity: 0, y: -10 }}

                      animate={{ opacity: 1, y: 0 }}

                      exit={{ opacity: 0, y: -10 }}

                      transition={{ duration: 0.2 }}

                      className="absolute z-50 w-full mt-2 bg-white border border-gray-200 rounded-lg shadow-lg"

                    >

                      <ul className="py-1">

                        {years.map((year) => (

                          <li key={year}>

                            <button

                              onClick={() => handleYearSelect(year)}

                              className="w-full px-4 py-3 text-left text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition-colors"

                            >

                              {year}

                            </button>

                          </li>

                        ))}

                      </ul>

                    </motion.div>

                  )}

                </AnimatePresence>

              </div>

            </div>



            {/* Mobile Search */}

            <div className="md:hidden mb-4">

              <div className="relative">

                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />

                <input

                  type="text"

                  placeholder="Search..."

                  className="w-full pl-9 pr-4 py-3 bg-white rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"

                />

              </div>

            </div>



            {/* Page Content */}

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 min-h-[calc(100vh-12rem)]">

              {children}

            </div>

          </div>

        </main>

      </div>

    </div>

  )

}

